<template>
    <div class="ufo--portfolio-password-protected">
        <div class="password-content">
            <div v-if="!isCaseStudy" class="password-meta user-name">
                {{ userName }}
            </div>
            <RouterLink v-else :to="homeLink" class="password-meta back-link">
                <Icon icon-name="back" :width="24" :height="24" />
                <span class="back-link__label">Back to portfolio</span>
            </RouterLink>

            <div class="lock-badge" aria-hidden="true">
                <Icon icon-name="password-lock" :width="24" :height="24" />
            </div>

            <h1 class="password-title">{{ passwordPageContent.title }}</h1>
            <div class="ufo-sec--contactform">
                <div class="ufo--contact-form">
                    <form name="password_form" class="ufo--form" @submit.prevent="checkPassword">
                        <div class="ufo-form--group">
                            <input
                                v-model="password"
                                name="password"
                                type="password"
                                autocomplete="off"
                                :placeholder="passwordPageContent.inputPlaceholder"
                                required
                                class="form-handler password-input password-page"
                                :style="inputStyle"
                            />
                            <div class="ufo--cta-btn-wr">
                                <button type="submit" :class="ctaButtonClasses">{{ passwordPageContent.buttonLabel }}</button>
                            </div>
                        </div>
                        <div v-if="passwordError" class="password-error">
                            <p>{{ passwordError }}</p>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ApiHelper from "../../../api/ApiHelper.js";
import {injectCssVars} from "@/scripts/common/utilities/cssVars.js";
import {mapGetters} from "vuex";
import Icon from "../../../common/Icon.vue";

const FIELD_RADIUS_BY_SHAPE = Object.freeze({
    square: 0,
    rounded: 8,
    pill: 40,
});

export default {
    name: "PasswordProtection",
    components: {
        Icon,
    },
    props: ['fetchUrl', 'setData', 'type'],
    data() {
        return {
            passwordError: false,
            password:'',
        }
    },
    watch: {
        designReady: {
            immediate: true,
            handler(isReady) {
                if (!isReady) {
                    return;
                }
                const cssVars = this.$store.getters["design/getAllCssVars"];
                if (cssVars) {
                    injectCssVars(cssVars);
                }
            }
        }
    },

    methods: {
        async checkPassword() {
            const url = this.fetchUrl(this.$route);
            try {
                const res = await ApiHelper.get(url, {password: this.password});
                this.onSuccess(res.data)
            }
            catch {
                this.onFailure()
            }
        },
        onFailure() {
            this.passwordError = 'Invalid password'
        },
        onSuccess(data) {
            if(this.type === 'portfolio') {
                this.$cookies.set(data.portfolio.shortId, this.password);
                if(data.portfolio.ownDomainAddress) {
                    this.$cookies.set(data.portfolio.ownDomainAddress, this.password);
                }
                let projectIdArray = data.relatedProjectIds.slice(0, -1).split(",");
                projectIdArray.forEach((projectId) => {
                    this.$cookies.set(projectId, this.password);
                })
                this.setData(null, data);
            }
            else {
                this.$cookies.set(data.project.shortId, this.password);
                this.setData(null, data);
            }
        }
    },
    computed: {
        ...mapGetters("view", ["viewData", "normalizedHost"]),
        ...mapGetters(["isCaseStudy"]),
        designReady() {
            return !!this.$store.state.portfolio?.designSettings;
        },
        ctaButtonClasses() {
            if (!this.designReady) {
                return ['btn', 'btn--cta'];
            }
            const primaryStyle = this.$store.getters["design/ctaStyles"]?.primary
                || this.$store.getters["design/ctaStyle"];
            return ['btn', 'btn--cta', primaryStyle?.fillStyle, primaryStyle?.shape, primaryStyle?.size];
        },
        pageType() {
            return this.isCaseStudy ? 'case study' : 'portfolio';
        },
        usesInheritedPortfolioPassword() {
            return this.isCaseStudy && !this.viewData.project?.password && !!this.viewData.portfolio?.password;
        },
        passwordPageContent() {
            const defaults = {
                title: `Please enter the password to view my ${this.pageType}.`,
                inputPlaceholder: 'Enter password',
                buttonLabel: 'Enter',
            };
            const settings = (
                this.usesInheritedPortfolioPassword
                    ? this.viewData.portfolio?.settings
                    : this.isCaseStudy
                        ? this.viewData.project?.settings
                        : this.viewData.portfolio?.settings
            )?.passwordPage;

            if (!settings?.enabled) {
                return defaults;
            }

            return {
                title: settings.title || defaults.title,
                inputPlaceholder: settings.inputPlaceholder || defaults.inputPlaceholder,
                buttonLabel: settings.buttonLabel || defaults.buttonLabel,
            };
        },
        userName() {
            return this.viewData.user?.name || '';
        },
        homeLink() {
            const portfolio = this.viewData.portfolio;
            return !portfolio || portfolio.ownDomainAddress?.toLowerCase() === this.normalizedHost
                ? '/'
                : `/${portfolio.shortId}`;
        },
        inputStyle() {
            return {
                borderRadius: `${this.inputBorderRadius}px`,
            };
        },
        inputBorderRadius() {
            const primaryStyle = this.$store.getters["design/ctaStyles"]?.primary
                || this.$store.getters["design/ctaStyle"];
            const shape = primaryStyle?.shape;
            const fillStyle = primaryStyle?.fillStyle;

            return fillStyle === "underline" ? 8 : (FIELD_RADIUS_BY_SHAPE[shape] ?? 0);
        }
    }
}
</script>

<style lang="scss" scoped>
@import "@/styles/components/cta-button";
@import "@/styles/components/sections/contact-form-section";

.ufo--portfolio-password-protected {
    width: 100%;
    max-width: 1440px;
    min-height: 100vh;
    margin: auto;
    background-color: var(--background-fill-color);
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    align-content: start;
    justify-items: stretch;
    padding: 80px 24px 24px;
    box-sizing: border-box;
    .password-content {
        grid-column: 4 / span 6;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        width: 100%;
        max-width: 532px;
        justify-self: center;
    }
    .password-meta {
        font-family: var(--section-h4-font);
        font-size: var(--section-h4-size);
        font-weight: var(--section-h4-weight);
        font-style: var(--section-h4-style);
        line-height: var(--section-h4-height);
        color: var(--section-h3-text-color);
        margin: 0 0 144px;
    }
    .back-link {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        text-decoration: none;
        &:hover {
            cursor: pointer;
        }

        .back-link__label {
            text-decoration: none;
            background-image: linear-gradient(currentColor, currentColor);
            background-repeat: no-repeat;
            background-position: bottom left;
            background-size: 0 2px;
            -webkit-box-decoration-break: clone;
            box-decoration-break: clone;
            transition: background-size 300ms cubic-bezier(0.22, 0.61, 0.36, 1);
        }

        &:hover {
            .back-link__label {
                background-size: 100% 2px;
            }
        }
    }
    .lock-badge {
        --fillColor: var(--section-h3-text-color);
        width: 62px;
        height: 62px;
        border-radius: 999px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 16px;
        color: var(--section-h3-text-color);
        background-color: color-mix(in srgb, var(--section-h3-text-color) 12%, transparent);
    }
    .password-title {
        font-family: var(--section-h3-font);
        font-size: var(--section-h3-size);
        font-weight: var(--section-h3-weight);
        font-style: var(--section-h3-style);
        line-height: var(--section-h3-height);
        text-align: center;
        white-space: pre-line;
        color: var(--section-h3-text-color);
        margin: 0 0 48px;
    }
    @media (max-width: 1024px) {
        .password-content {
            grid-column: 3 / span 8;
        }
    }
    @media (max-width: 768px) {
        .password-content {
            grid-column: 1 / -1;
        }
    }
    @media (max-width: 1024px) {
        .password-meta {
            font-size: var(--section-h4-size-tablet);
            line-height: var(--section-h4-height-tablet);
            margin-bottom: 96px;
        }
        .password-title {
            font-size: var(--section-h3-size-tablet);
            line-height: var(--section-h3-height-tablet);
        }
    }
    @media (max-width: 768px) {
        .password-meta {
            font-size: var(--section-h4-size-mobile);
            line-height: var(--section-h4-height-mobile);
            margin-bottom: 64px;
        }
        .password-title {
            font-size: var(--section-h3-size-mobile);
            line-height: var(--section-h3-height-mobile);
        }
    }
    .ufo-sec--contactform,
    .ufo--contact-form,
    form {
        width: 100%;
        max-width: 100%;
    }
    .ufo-form--group {
        display: flex;
        flex-direction: row;
        align-items: center;
        width: 100%;
        gap: 12px;
        .password-input {
            width: 100%;
            margin: 0;
            flex: 1 1 0;
            border-color: color-mix(in srgb, var(--section-h3-text-color) 18%, transparent);
            color: var(--section-p1-text-color);
            background-color: transparent;
            padding: 12px 18px !important;
            line-height: 24px !important;
            font-size: 16px !important;
            &::placeholder {
                color: var(--section-p1-text-color);
            }
        }
        .ufo--cta-btn-wr {
            margin-top: 0;
            width: auto;
            flex: 0 0 auto;
            justify-content: flex-start;
            padding: 0;
        }
        .btn--cta {
            width: 100%;
            font-size: 16px !important;
            line-height: 24px !important;
        }

        @media (max-width: 640px) {
            flex-direction: column;
            align-items: stretch;

            .ufo--cta-btn-wr {
                width: 100%;
            }

            .btn--cta {
                width: 100%;
            }
        }
    }
}
</style>